import { Menu, Moon, Sun, X, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import logo from "@/assets/logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavChild {
  label: string;
  href: string;
  hidden?: boolean;
}

interface NavItem {
  label: string;
  href: string;
  children?: NavChild[];
}

const navLinks: NavItem[] = [
  { label: "Home", href: "/" },
  {
    label: "Playbook",
    href: "/playbook",
    children: [
      { label: "I Have an Idea", href: "/playbook/i-have-an-idea" },
      { label: "I Don't Have an Idea", href: "/playbook/no-idea" },
    ],
  },
  {
    label: "Tools",
    href: "/tools",
    children: [
      { label: "Tool Directory", href: "/tools" },
      { label: "AI Courses", href: "/tools/ai-courses" },
    ],
  },
  { label: "Builders", href: "/builders" },
  {
    label: "Co-Found",
    href: "/co-found",
    children: [
      { label: "Fund Library", href: "/co-found/fund-library" },
      { label: "Pitch Deck Builder", href: "/co-found/pitch-deck-builder" },
      { label: "The Pledge", href: "/co-found/the-pledge", hidden: true },
    ],
  },
  { label: "About", href: "/about" },
  { label: "Blog", href: "/blog" },
];

const getInitialTheme = (): "dark" | "light" => {
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem("theme");
  if (stored === "dark" || stored === "light") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const isActive = (pathname: string, href: string) => pathname === href;

const isParentActive = (pathname: string, item: NavItem) => {
  if (pathname === item.href) return true;
  if (item.children?.some((c) => pathname === c.href)) return true;
  return false;
};

const Navbar = () => {
  const [isDark, setIsDark] = useState<boolean>(() => getInitialTheme() === "dark");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const theme = isDark ? "dark" : "light";
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("theme", theme);
  }, [isDark]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav className="brutal-border border-t-0 border-l-0 border-r-0 bg-background sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-1 font-black text-lg tracking-tight text-foreground shrink-0 mr-4">
          <img src={logo} alt="Co-Found with Us logo" width={40} height={40} className="w-10 h-10 rounded-md shrink-0" />
          <span className="font-black tracking-tight whitespace-nowrap">Co-Found with Us</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((item) => {
            if (item.children && item.children.length > 0) {
              const visibleChildren = item.children.filter((c) => !c.hidden);
              return (
                <DropdownMenu key={item.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`font-bold text-xs xl:text-sm tracking-wide px-2 xl:px-4 py-2 rounded-md transition-colors uppercase tracking-[0.08em] flex items-center gap-1 whitespace-nowrap ${
                        isParentActive(location.pathname, item)
                          ? "text-primary"
                          : "text-foreground hover:text-primary"
                      }`}
                    >
                      {item.label}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="brutal-border bg-popover min-w-[12rem]"
                  >
                    {visibleChildren.map((child) => (
                      <DropdownMenuItem key={child.href} asChild>
                        <Link
                          to={child.href}
                          className={`font-bold text-sm uppercase tracking-[0.08em] cursor-pointer ${
                            isActive(location.pathname, child.href)
                              ? "text-primary"
                              : "text-foreground hover:text-primary"
                          }`}
                        >
                          {child.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`font-bold text-xs xl:text-sm tracking-wide px-2 xl:px-4 py-2 rounded-md transition-colors uppercase tracking-[0.08em] whitespace-nowrap ${
                  isActive(location.pathname, item.href)
                    ? "text-primary"
                    : "text-foreground hover:text-primary"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/playbook/i-have-an-idea"
            className="hidden sm:inline-flex brutal-border brutal-shadow bg-primary text-primary-foreground font-bold px-3 py-1.5 text-xs hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-md uppercase tracking-[0.08em]"
          >
            Get the Playbook →
          </Link>

          <button
            onClick={() => setIsDark(!isDark)}
            className="brutal-border brutal-shadow min-w-11 min-h-11 w-11 h-11 flex items-center justify-center bg-card hover:bg-muted transition-colors rounded-md"
            aria-label="Toggle dark mode"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden brutal-border brutal-shadow min-w-11 min-h-11 w-11 h-11 flex items-center justify-center bg-card hover:bg-muted transition-colors rounded-md"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          id="mobile-nav"
          className="lg:hidden border-t-2 border-foreground bg-background"
        >
          <div className="container mx-auto px-4 py-3 flex flex-col gap-1">
            {navLinks.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const visibleChildren = item.children?.filter((c) => !c.hidden) ?? [];

              if (hasChildren) {
                return (
                  <div key={item.label} className="flex flex-col gap-1">
                    <span className="font-bold text-sm px-3 py-2 uppercase tracking-[0.08em] text-muted-foreground">
                      {item.label}
                    </span>
                    {visibleChildren.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={() => setMobileOpen(false)}
                        className={`font-bold text-base px-3 py-3 min-h-11 rounded-md transition-colors uppercase tracking-[0.08em] ml-4 ${
                          isActive(location.pathname, child.href)
                            ? "bg-primary text-primary-foreground"
                            : "text-foreground hover:bg-muted"
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`font-bold text-base px-3 py-3 min-h-11 rounded-md transition-colors uppercase tracking-[0.08em] ${
                    isActive(location.pathname, item.href)
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/playbook/i-have-an-idea"
              onClick={() => setMobileOpen(false)}
              className="sm:hidden mt-2 brutal-border brutal-shadow-lg bg-primary text-primary-foreground font-bold px-5 py-2 text-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-md uppercase tracking-[0.08em] text-center"
            >
              Get the Playbook →
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
